import React, { useEffect, useState } from "react";
import { useAlert } from "react-alert";
import { XCircle, RefreshCw, Clock, UserPlus } from "lucide-react";
import moment from "moment";
import { Loader } from "./loader";
import api from "../../api";

export default function CanceledSubs24h() {
  const alert = useAlert();
  const [loader, setLoader] = useState(false);
  const [canceledSubs, setCanceledSubs] = useState([]);
  const [newUsers, setNewUsers] = useState([]);

  const fetchData = async () => {
    try {
      setLoader(true);
      const res = await api.getCanceledSubs24h();
      if (res?.success) {
        setCanceledSubs((res.canceledSubs || []).filter((s) => s.reason !== "payment_failed"));
        setNewUsers(res.newUsers || []);
      } else {
        alert.error("Failed to load data");
      }
    } catch (e) {
      alert.error(e?.message || "Something went wrong");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const sinceLabel = moment().subtract(24, "hours").format("DD MMM YYYY, HH:mm");

  const reasonBadge = (reason) => {
    return (
      <span style={{ background: "#fef2f2", color: "#ef4444", padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
        Canceled
      </span>
    );
  };

  return (
    <div style={{ padding: "32px 24px", background: "#f9fafb", minHeight: "100vh" }}>

      {/* Page header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: "#111827", margin: 0 }}>24 Hours Data</h1>
          <p style={{ fontSize: 13, color: "#6b7280", margin: "4px 0 0", display: "flex", alignItems: "center", gap: 5 }}>
            <Clock size={13} /> Since {sinceLabel}
          </p>
        </div>
        <button
          onClick={fetchData}
          disabled={loader}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "9px 18px", borderRadius: 10, border: "none",
            background: "#7c3aed", color: "#fff", fontWeight: 700,
            fontSize: 13, cursor: "pointer", opacity: loader ? 0.6 : 1,
          }}
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Count boxes */}
      <div style={{ display: "flex", gap: 16, marginBottom: 32, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, background: "#fff", borderRadius: 16, padding: "20px 28px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", border: "1px solid #f3f4f6" }}>
          <div style={{ background: "#fef2f2", borderRadius: 12, padding: 14 }}>
            <XCircle size={30} color="#ef4444" />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>
              Cancellations
            </div>
            <div style={{ fontSize: 36, fontWeight: 800, color: "#111827", lineHeight: 1 }}>
              {loader ? "—" : canceledSubs.length}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16, background: "#fff", borderRadius: 16, padding: "20px 28px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", border: "1px solid #f3f4f6" }}>
          <div style={{ background: "#f0fdf4", borderRadius: 12, padding: 14 }}>
            <UserPlus size={30} color="#22c55e" />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>
              New Registrations
            </div>
            <div style={{ fontSize: 36, fontWeight: 800, color: "#111827", lineHeight: 1 }}>
              {loader ? "—" : newUsers.length}
            </div>
          </div>
        </div>
      </div>

      {loader ? <Loader /> : (
        <>
          {/* Canceled subscriptions table */}
          <h2 style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 12 }}>
            Canceled Subscriptions
          </h2>
          {canceledSubs.length === 0 ? (
            <div style={{ background: "#fff", borderRadius: 16, padding: "40px 0", textAlign: "center", color: "#9ca3af", fontSize: 14, border: "1px solid #f3f4f6", marginBottom: 32 }}>
              No cancellations in the last 24 hours
            </div>
          ) : (
            <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden", border: "1px solid #f3f4f6", marginBottom: 36 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                    {["#", "Email", "Subscription ID", "Canceled At", "Reason"].map((h) => (
                      <th key={h} style={{ padding: "13px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#6b7280", letterSpacing: 0.8, textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {canceledSubs.map((row, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "12px 16px", fontSize: 13, color: "#9ca3af" }}>{i + 1}</td>
                      <td style={{ padding: "12px 16px", fontSize: 13, color: "#111827", fontWeight: 600 }}>{row.email || "—"}</td>
                      <td style={{ padding: "12px 16px", fontSize: 12, color: "#6b7280", fontFamily: "monospace" }}>{row.subscriptionId}</td>
                      <td style={{ padding: "12px 16px", fontSize: 13, color: "#374151" }}>
                        {row.canceledAt ? moment.unix(row.canceledAt).format("DD MMM YYYY, HH:mm") : "—"}
                      </td>
                      <td style={{ padding: "12px 16px" }}>{reasonBadge(row.reason)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* New registrations table */}
          <h2 style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 12 }}>
            New Registrations
          </h2>
          {newUsers.length === 0 ? (
            <div style={{ background: "#fff", borderRadius: 16, padding: "40px 0", textAlign: "center", color: "#9ca3af", fontSize: 14, border: "1px solid #f3f4f6" }}>
              No new registrations in the last 24 hours
            </div>
          ) : (
            <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden", border: "1px solid #f3f4f6" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                    {["#", "Name", "Email", "Phone", "Country", "Joined At"].map((h) => (
                      <th key={h} style={{ padding: "13px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "#6b7280", letterSpacing: 0.8, textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {newUsers.map((u, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "12px 16px", fontSize: 13, color: "#9ca3af" }}>{i + 1}</td>
                      <td style={{ padding: "12px 16px", fontSize: 13, color: "#111827", fontWeight: 600 }}>{u.name || "—"}</td>
                      <td style={{ padding: "12px 16px", fontSize: 13, color: "#374151" }}>{u.email || "—"}</td>
                      <td style={{ padding: "12px 16px", fontSize: 13, color: "#374151" }}>{u.phone || "—"}</td>
                      <td style={{ padding: "12px 16px", fontSize: 13, color: "#374151" }}>{u.country || "—"}</td>
                      <td style={{ padding: "12px 16px", fontSize: 13, color: "#374151" }}>
                        {u.createdAt ? moment(u.createdAt).format("DD MMM YYYY, HH:mm") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
